import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
    CircularProgress,
    FormLabel,
    IconButton,
    InputAdornment,
    Stack,
    type StackProps,
    TextField,
    Tooltip,
    type TextFieldProps,
} from "@mui/material";
import { useField } from "formik";
import { useEffect, useState, type ChangeEvent, type FC } from "react";

import "./FormText.scss";

export interface FormTextFieldProps extends TextFieldProps<"standard"> {
    name: string;
    label: string;
    withInfo?: string;
    loading?: boolean;
    containerProps?: StackProps;
    helperError?: boolean;
}

export const FormTextField: FC<FormTextFieldProps> = (props) => {
    const { name, label, withInfo, loading = false, helperError = true, containerProps, ...restInputProps } = props;
    const [field, meta, helpers] = useField(name);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    useEffect(() => {
        if (tooltipOpen) {
            const timeout = setTimeout(() => {
                setTooltipOpen(false);
            }, 4000);
            return () => clearTimeout(timeout);
        }
    }, [tooltipOpen]);

    function handleChange(
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) {
        if (restInputProps.onChange) restInputProps.onChange(event);
        helpers.setValue(event.target.value);
    }

    function onInfoClick() {
        setTooltipOpen((prev) => !prev);
    }

    return (
        <Stack className="field-container" {...containerProps}>
            {label && (
                <FormLabel className="field-label" htmlFor={name}>
                    {label}
                    {withInfo && (
                        <Tooltip
                            PopperProps={{ className: "field-tooltip" }}
                            componentsProps={{ tooltip: { className: "field-tooltip" } }}
                            onClick={onInfoClick}
                            placement="top-end"
                            open={tooltipOpen}
                            slotProps={{
                                tooltip: {
                                    style: { marginBottom: 0 },
                                },
                            }}
                            title={withInfo}
                        >
                            <IconButton
                                type="button"
                                className="info-button"
                                onClick={onInfoClick}
                            >
                                <InfoOutlined
                                    className="info-icon"
                                    sx={{ fontSize: "1.2rem" }}
                                />
                            </IconButton>
                        </Tooltip>
                    )}
                </FormLabel>
            )}
            <TextField
                {...field}
                {...restInputProps}
                FormHelperTextProps={{ className: "helper-text" }}
                id={name}
                InputProps={{
                    ...restInputProps.InputProps,
                    className: `field ${restInputProps.InputProps?.className || ""} ${restInputProps.multiline ? "multiline" : ""}`,
                    endAdornment: (
                        <InputAdornment position="end">
                            {restInputProps.InputProps?.endAdornment}
                            {loading && <CircularProgress size={18} />}
                        </InputAdornment>
                    ),
                }}
                error={!!meta.error}
                helperText={helperError && meta.touched && meta.error}
                onChange={handleChange}
            />
        </Stack>
    );
};